import React, { useState, useEffect } from 'react';
import { TiArrowUnsorted } from "react-icons/ti";
import { TbPencil } from "react-icons/tb";
import { FaCaretDown, FaRegTrashAlt } from "react-icons/fa";
import Select from 'react-select';
import { useCurrency } from '../../../../../../../context/currencyContext';
import './OutsourcePricing.css';

const OutsourcePricing = () => {
  // Get currency context
  const { currency, formatAmount } = useCurrency();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    price: 0,
    pricingFor: 'Multiple Labs',
    selectedLabs: [],
    extCost: 0,
    extPID: '',
    extVPID: '',
    extDesc: '',
  });

  const [availableLabs, setAvailableLabs] = useState([]);
  const [existingPricing, setExistingPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState('');
  const [editingPricingId, setEditingPricingId] = useState(null);

  // Delete dropdown menu state: { id, top, right } or null
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => {
    fetchLabs();
    fetchExistingPricing();
  }, []);

  // Close dropdown on outside click / scroll / resize
  useEffect(() => {
    if (!openMenu) return;

    const handleOutside = (e) => {
      if (!e.target.closest('.op-menu-dropdown') && !e.target.closest('.op-split-toggle')) {
        setOpenMenu(null);
      }
    };
    const handleClose = () => setOpenMenu(null);

    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
    };
  }, [openMenu]);

  // Toggle Delete dropdown under the split button
  const toggleMenu = (e, pricingId) => {
    e.stopPropagation();
    if (openMenu && openMenu.id === pricingId) {
      setOpenMenu(null);
      return;
    }
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    setOpenMenu({
      id: pricingId,
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  };

  // Helper function to get auth token from localStorage
  const getAuthToken = () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        return accessToken;
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedUserData = JSON.parse(userData);
        if (parsedUserData.token) {
          return parsedUserData.token;
        }
      }

      return null;
    } catch (err) {
      return null;
    }
  };

  // Get current user data (the creator)
  const getCurrentUserData = () => {
    try {
      let userLabId = null;
      let userName = '';
      let userId = null;
      let userData = {};

      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        userData = JSON.parse(userDataStr);
        userLabId = userData.userLabId;
        userName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
        userId = userData.userId;
      }

      if (!userLabId) {
        userLabId = localStorage.getItem('userLabId');
      }

      if (!userLabId) {
        userLabId = sessionStorage.getItem('userLabId');
      }

      if (userLabId) {
        userLabId = String(userLabId).padStart(8, '0');
      }

      const result = {
        userLabId: userLabId,
        userName: userName || userData.username || 'Unknown',
        userId: userId || userData.userId,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        fullUserData: userData
      };

      return result;
    } catch (err) {
      return { userLabId: null, userName: 'Unknown', userId: null };
    }
  };

  // Fetch existing outsource pricing
  const fetchExistingPricing = async () => {
    const procedureMongoId = sessionStorage.getItem('procedureId');
    if (!procedureMongoId) {
      return;
    }

    try {
      const accessToken = getAuthToken();
      if (!accessToken) {
        return;
      }

      const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/outsource-pricing/procedure/${procedureMongoId}/outsource`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
      });

      if (response.ok) {
        const result = await response.json();
        setExistingPricing(result.data || []);
      } else {
        const errorData = await response.json();
        // Silently handle error
      }
    } catch (error) {
      // Silently handle error
    }
  };

  // Regular function, not a Hook
  const setMockData = () => {
    const mockLabs = [
      { value: '1', label: 'Bardha Jas Lab', labData: { _id: '1', labName: 'Bardha Jas Lab' } },
      { value: '2', label: 'Diu Resh Lab', labData: { _id: '2', labName: 'Diu Resh Lab' } },
      { value: '3', label: 'Roebuck Louise Lab', labData: { _id: '3', labName: 'Roebuck Louise Lab' } },
      { value: '4', label: 'T Hyare Lab', labData: { _id: '4', labName: 'T Hyare Lab' } },
      { value: '5', label: 'Main Laboratory', labData: { _id: '5', labName: 'Main Laboratory' } },
    ];
    setAvailableLabs(mockLabs);
    setError('Using mock data - Backend API requires authentication');
  };

  const fetchLabs = async () => {
    setLoading(true);
    setError(null);
    setApiStatus('Fetching labs...');

    try {
      const accessToken = getAuthToken();

      if (!accessToken) {
        setApiStatus('⚠️ Not authenticated. Please log in first.');
        setError('Authentication required. Please log in to fetch lab data.');
        setMockData();
        setLoading(false);
        return;
      }

      const endpoints = [
        'https://hms.backend.hiragroupcloud.com/api/auth/admins',
        '/api/auth/admins',
      ];

      let response = null;
      let successfulEndpoint = '';

      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            credentials: 'include',
            mode: 'cors',
          });

          if (res.ok) {
            response = res;
            successfulEndpoint = endpoint;
            break;
          } else if (res.status === 401 || res.status === 403) {
            continue;
          }
        } catch (err) {
          continue;
        }
      }

      if (!response) {
        setApiStatus('⚠️ Authentication failed. Using mock data for development.');
        setError('Unable to authenticate. Please check your login status.');
        setMockData();
        return;
      }

      const data = await response.json();

      let labs = [];

      // Get current logged-in user's lab ID
      const currentUser = getCurrentUserData();
      const currentUserLabId = currentUser.userLabId;

      if (data.success && data.users) {
        labs = data.users
          .filter(user => {
            // remove logged-in user's lab
            return user.labName && String(user.userLabId).padStart(8, '0') !== currentUserLabId;
          })
          .map(user => ({
            value: user._id,
            label: user.labName,
            labData: user
          }));
      } else if (Array.isArray(data)) {
        labs = data
          .filter(item => item.labName)
          .map(item => ({
            value: item._id,
            label: item.labName,
            labData: item
          }));
      } else if (data.data && Array.isArray(data.data)) {
        labs = data.data
          .filter(item => item.labName)
          .map(item => ({
            value: item._id,
            label: item.labName,
            labData: item
          }));
      }

      if (labs.length === 0) {
        setApiStatus('No labs found in database');
        setError('No labs found. Please add labs first.');
        setMockData();
      } else {
        setAvailableLabs(labs);
        setApiStatus(`✅ Loaded ${labs.length} labs successfully`);
        setError(null);
      }

    } catch (err) {
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setApiStatus('⚠️ Backend not running. Using mock data.');
      } else if (err.message.includes('401') || err.message.includes('403')) {
        setApiStatus('🔒 Auth required. Using mock data.');
      } else {
        setApiStatus(`❌ Error: ${err.message}. Using mock data.`);
      }

      setMockData();
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    setOpenMenu(null);
    setIsModalOpen(true);
    setEditingPricingId(null);
    setFormData({
      price: 0,
      pricingFor: 'Multiple Labs',
      selectedLabs: [],
      extCost: 0,
      extPID: '',
      extVPID: '',
      extDesc: '',
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPricingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'price' || name === 'extCost' ? parseFloat(value) || 0 : value
    }));
  };

  const handleLabSelectChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      selectedLabs: selectedOptions || []
    }));
  };

  // Handle edit
  const handleEdit = (pricing) => {
    setOpenMenu(null);
    setEditingPricingId(pricing._id);

    // Map selected labs back to react-select format
    const selectedLabOptions = pricing.selectedLabs?.map(lab => ({
      value: lab.labId,
      label: lab.labName,
      labData: { _id: lab.labId, labName: lab.labName }
    })) || [];

    setFormData({
      price: pricing.price,
      pricingFor: pricing.pricingFor,
      selectedLabs: selectedLabOptions,
      extCost: pricing.extCost || 0,
      extPID: pricing.extPID || '',
      extVPID: pricing.extVPID || '',
      extDesc: pricing.extDesc || '',
    });

    setIsModalOpen(true);
  };

  // Handle delete
  const handleDelete = async (pricingId) => {
    if (!window.confirm('Are you sure you want to delete this pricing?')) {
      return;
    }

    const procedureMongoId = sessionStorage.getItem('procedureId');
    if (!procedureMongoId) {
      alert('No procedure selected');
      return;
    }

    try {
      setLoading(true);
      const accessToken = getAuthToken();

      const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/outsource-pricing/procedure/${procedureMongoId}/outsource/${pricingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
      });

      const result = await response.json();

      if (response.ok) {
        alert('Pricing deleted successfully!');
        await fetchExistingPricing();
      } else {
        alert(`Failed to delete pricing: ${result.message}`);
      }
    } catch (error) {
      alert('Failed to delete pricing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle submit (add or update)
  const handleSubmit = async () => {
    if (formData.pricingFor === 'Multiple Labs' && formData.selectedLabs.length === 0) {
      alert('Please select at least one lab');
      return;
    }

    if (formData.price <= 0) {
      alert('Please enter a valid price greater than 0');
      return;
    }

    const procedureMongoId = sessionStorage.getItem('procedureId');

    if (!procedureMongoId) {
      alert('No procedure selected. Please select a procedure first.');
      return;
    }

    // Get current user data (the creator)
    const currentUser = getCurrentUserData();
    const creatorUserLabId = currentUser.userLabId;
    const creatorUserName = currentUser.userName;
    const creatorUserId = currentUser.userId;

    if (!creatorUserLabId) {
      alert('User identification not found. Please log in again.');
      return;
    }

    const submitData = {
      price: formData.price,
      pricingFor: formData.pricingFor,
      selectedLabs: formData.selectedLabs.map(lab => lab.value),
      extCost: formData.extCost,
      extPID: formData.extPID,
      extVPID: formData.extVPID,
      extDesc: formData.extDesc,
      // Method 1: Send as creatorInfo object
      creatorInfo: {
        userLabId: creatorUserLabId,
        userName: creatorUserName,
        userId: creatorUserId
      },
      // Method 2: Send directly in body (backward compatibility)
      userLabId: creatorUserLabId,
      userName: creatorUserName,
      userId: creatorUserId
    };

    try {
      setLoading(true);
      const accessToken = getAuthToken();

      const url = editingPricingId
        ? `https://hms.backend.hiragroupcloud.com/api/outsource-pricing/procedure/${procedureMongoId}/outsource/${editingPricingId}`
        : `https://hms.backend.hiragroupcloud.com/api/outsource-pricing/procedure/${procedureMongoId}/outsource`;

      const response = await fetch(url, {
        method: editingPricingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(submitData),
      });

      const result = await response.json();

      if (response.ok) {
        alert(editingPricingId ? 'Outsource pricing updated successfully!' : 'Outsource pricing added successfully!');

        await fetchExistingPricing();
        closeModal();

        setFormData({
          price: 0,
          pricingFor: 'Multiple Labs',
          selectedLabs: [],
          extCost: 0,
          extPID: '',
          extVPID: '',
          extDesc: '',
        });
      } else {
        alert(`Failed to ${editingPricingId ? 'update' : 'add'} pricing: ${result.message}`);
      }
    } catch (error) {
      alert('Failed to save pricing. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      border: '1px solid #ccc',
      borderRadius: '3px',
      fontSize: '14px',
      minHeight: '38px',
      width: '100%',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e8e8e8',
      borderRadius: '16px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      fontSize: '13px',
      color: '#333',
      fontWeight: '500',
    }),
    multiValueRemove: (base) => ({
      ...base,
      borderRadius: '0 16px 16px 0',
      cursor: 'pointer',
      ':hover': {
        backgroundColor: '#ff4444',
        color: 'white',
      },
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '13px',
      color: '#999',
    }),
    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  };

  // Helper function to display currency symbol
  const getCurrencySymbol = () => {
    return currency?.currencySymbol || '₮';
  };

  return (
    <div className="outsourcing-pricing-container">
      <div className="op-table-header">
        <div className="op-first-header-cell">
          Price ({getCurrencySymbol()}) <TiArrowUnsorted className="op-sort-icon" />
        </div>
        <div className="op-header-cell">
          Default/Lab <TiArrowUnsorted className="op-sort-icon" />
        </div>
        <div className="op-header-cell">
          Pricing For <TiArrowUnsorted className="op-sort-icon" />
        </div>
        <div className="op-header-cell">
          Attributes <TiArrowUnsorted className="op-sort-icon" />
        </div>
        <div className="op-header-cell">
          Ext. Cost ({getCurrencySymbol()}) <TiArrowUnsorted className="op-sort-icon" />
        </div>
        <div className="op-header-cell">
          External Data <TiArrowUnsorted className="op-sort-icon" />
        </div>
        <div className="op-header-cell">
          Actions
        </div>
      </div>

      {/* Display existing pricing rows */}
      {existingPricing && existingPricing.length > 0 ? (
        existingPricing.map((pricing, index) => (
          <div key={pricing._id || index} className="op-row">
            <div className="op-first-row-cell">{getCurrencySymbol()}{pricing.price}</div>
            <div className="op-row-cell">
              {pricing.pricingFor === 'Default' ? 'All Labs' :
               pricing.selectedLabs?.map(lab => lab.labName).join(', ') || 'N/A'}
            </div>
            <div className="op-row-cell">{pricing.pricingFor}</div>
            <div className="op-row-cell">-</div>
            <div className="op-row-cell">{getCurrencySymbol()}{pricing.extCost || 0}</div>
            <div className="op-row-cell op-ext-cell">
              {pricing.extPID && <div className="op-ext-line">PID: {pricing.extPID}</div>}
              {pricing.extVPID && <div className="op-ext-line">VPID: {pricing.extVPID}</div>}
              {pricing.extDesc && <div className="op-ext-line">{pricing.extDesc}</div>}
              {!pricing.extPID && !pricing.extVPID && !pricing.extDesc && '-'}
            </div>
            <div className="op-row-cell op-actions-cell">
              {/* Split button: Edit | arrow (Delete dropdown) */}
              <div className="op-split-btn">
                <button
                  type="button"
                  onClick={() => handleEdit(pricing)}
                  className="op-split-main"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={(e) => toggleMenu(e, pricing._id)}
                  className="op-split-toggle"
                  aria-label="More actions"
                >
                  <FaCaretDown />
                </button>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="op-row">
          <div className="op-first-row-cell">-</div>
          <div className="op-row-cell">-</div>
          <div className="op-row-cell">-</div>
          <div className="op-row-cell">-</div>
          <div className="op-row-cell">-</div>
          <div className="op-row-cell">-</div>
          <div className="op-row-cell">-</div>
        </div>
      )}

      {/* Delete dropdown (fixed position so it never gets clipped) */}
      {openMenu && (
        <div
          className="op-menu-dropdown"
          style={{ top: openMenu.top, right: openMenu.right }}
        >
          <button
            type="button"
            className="op-menu-item"
            onClick={() => {
              const id = openMenu.id;
              setOpenMenu(null);
              handleDelete(id);
            }}
          >
            <FaRegTrashAlt className="op-menu-icon" /> Delete
          </button>
        </div>
      )}

      <button className="op-add-btn" onClick={openModal}>+ Add</button>

      <div className="op-note">
        * You will need to click the save button below to actually save the pricing.
      </div>

      <div className="op-section-header">Invoice Details</div>
      <div className="op-section">
        <label>
          <input type="checkbox" /> Roll-up Attribute Prices into Procedure on Invoice
        </label>
      </div>

      <div className="op-section-header">Tax Details</div>
      <div className="op-section">
        Taxable?
      </div>

      <div className="op-section-header">Revenue Centers Details</div>
      <div className="op-section">
        Revenue Centers
        <span className="op-revenue-select">Select Revenue Centers</span>
      </div>

      <div className="op-attribute-header">Attribute Prices</div>
      <div className="op-attribute-section">
        <div className="op-attribute-text">No attribute prices</div>
      </div>

      <button className="op-save-btn">
        <TbPencil className="op-save-icon" /> Save All Changes
      </button>

      {isModalOpen && (
        <div className="op-modal-overlay">
          <div className="op-dialog">
            <div className="op-dialog-header">
              <span>{editingPricingId ? 'Edit Price' : 'Add Price'}</span>
              <span className="op-dialog-close" onClick={closeModal}>&times;</span>
            </div>
            <div className="op-dialog-content">
              <div className="op-section-header-modal">General</div>

              <div className="op-form-row">
                <label className="op-form-label">Price ({getCurrencySymbol()})</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="op-form-number"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="op-form-row">
                <label className="op-form-label">Pricing For</label>
                <select
                  name="pricingFor"
                  value={formData.pricingFor}
                  onChange={handleInputChange}
                  className="op-form-select"
                >
                  <option value="Multiple Labs">Select Labs</option>
                  <option value="Default">Default</option>
                </select>
              </div>

              {formData.pricingFor === 'Multiple Labs' && (
                <div className="op-form-row">
                  <label className="op-form-label">Select Labs</label>
                  <div className="op-form-field">
                    <Select
                      isMulti
                      name="labs"
                      options={availableLabs}
                      value={formData.selectedLabs}
                      onChange={handleLabSelectChange}
                      styles={selectStyles}
                      placeholder={loading ? "Loading labs..." : "Select one or more labs..."}
                      isLoading={loading}
                      isDisabled={loading}
                      noOptionsMessage={() => "No labs available"}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      menuPortalTarget={document.body}
                    />
                    {error && (
                      <div className="op-error-text">
                        ⚠️ {error}
                        <button
                          onClick={fetchLabs}
                          className="op-retry-btn"
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    <div className="op-hint-text">
                      You can select multiple labs by clicking on them
                    </div>
                  </div>
                </div>
              )}

              {formData.pricingFor === 'Default' && (
                <div className="op-form-row op-default-note">
                  <span className="op-form-label"></span>
                  <span>Default pricing will apply to all labs without specific pricing</span>
                </div>
              )}

              <div className="op-section-header-modal">Pricing Attributes</div>
              <div className="op-description-text">
                Choose procedure attributes that this price is dependent on, if any.
              </div>

              <div className="op-section-header-modal">External data</div>

              <div className="op-form-row">
                <label className="op-form-label">Ext. Cost ({getCurrencySymbol()})</label>
                <input
                  type="number"
                  name="extCost"
                  value={formData.extCost}
                  onChange={handleInputChange}
                  className="op-form-number"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="op-form-row">
                <label className="op-form-label">Ext. PID</label>
                <input
                  type="text"
                  name="extPID"
                  value={formData.extPID}
                  onChange={handleInputChange}
                  className="op-form-input"
                  placeholder="External Product ID"
                />
              </div>

              <div className="op-form-row">
                <label className="op-form-label">Ext. VPID</label>
                <input
                  type="text"
                  name="extVPID"
                  value={formData.extVPID}
                  onChange={handleInputChange}
                  className="op-form-input"
                  placeholder="External Vendor PID"
                />
              </div>

              <div className="op-form-row">
                <label className="op-form-label">Ext. Desc.</label>
                <input
                  type="text"
                  name="extDesc"
                  value={formData.extDesc}
                  onChange={handleInputChange}
                  className="op-form-input"
                  placeholder="External Description"
                />
              </div>
            </div>
            <div className="op-dialog-footer">
              <button className="op-cancel-btn" onClick={closeModal}>Cancel</button>
              <button className="op-add-btn-modal" onClick={handleSubmit}>
                {editingPricingId ? 'Update Price' : 'Add Price'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutsourcePricing;